import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { Loader2, CreditCard, ShieldCheck, FlaskConical, CheckCircle2, Circle, ExternalLink } from 'lucide-react';

const STEPS = [
  { t: 'Activate your Stripe account', d: 'In the Stripe Dashboard, complete business details and bank info so you can accept real payments (Dashboard → Activate account).' },
  { t: 'Get your LIVE secret key', d: 'Toggle Stripe Dashboard from "Test mode" to "Live", then Developers → API keys → copy the Live secret key (starts with sk_live_).' },
  { t: 'Swap the key in Larkwell', d: 'Replace STRIPE_API_KEY in the backend environment with your sk_live_ key, then restart the backend. This card will flip to LIVE.' },
  { t: 'Point the webhook at Larkwell', d: 'In Stripe → Developers → Webhooks, add an endpoint for this app\'s /api/webhook/stripe URL and enable checkout.session.completed.' },
  { t: 'Run one real test charge', d: 'Buy the cheapest package with a real card, confirm the plan/credits unlock, then refund yourself from Stripe if you like.' },
  { t: 'Confirm the receipt email', d: 'Check that the payment receipt email arrives (Resend). Receipts fire automatically on successful checkout.' },
];

export default function StripeGoLive() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem('larkwell_golive_checklist') || '{}'); } catch { return {}; }
  });

  const load = useCallback(async () => {
    try { const r = await api.get('/admin/stripe-status'); setStatus(r.data); }
    catch (e) { if (e?.response?.status !== 403) console.debug('stripe-status load failed', e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    try { localStorage.setItem('larkwell_golive_checklist', JSON.stringify(next)); } catch (e) { console.debug('checklist persist failed', e); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5 flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="stripe-golive-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking Stripe mode…
      </div>
    );
  }
  if (!status) return null;

  const isLive = status.mode === 'live';
  const doneCount = STEPS.filter((_, i) => checked[i]).length;

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5" data-testid="stripe-golive">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'hsl(var(--accent))' }}><CreditCard className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-600">Payments &middot; Go live</h3>
            <Badge variant="secondary" className="text-[10px]">Owner</Badge>
            {isLive ? (
              <Badge className="text-[10px] gap-1 text-white" style={{ background: 'hsl(var(--success))' }} data-testid="stripe-mode-badge"><ShieldCheck className="h-3 w-3" /> LIVE</Badge>
            ) : (
              <Badge className="text-[10px] gap-1 text-white" style={{ background: 'hsl(var(--brand-honey))' }} data-testid="stripe-mode-badge"><FlaskConical className="h-3 w-3" /> TEST MODE</Badge>
            )}
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {isLive
              ? 'Stripe is live — real cards will be charged. Nice work!'
              : 'You are in safe test mode. No real money moves. Follow the steps below when you\'re ready to charge.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center" data-testid="stripe-stat-mode"><p className="font-display text-sm font-700 uppercase">{status.mode}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">current mode</p></div>
        <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center"><p className="font-display text-xl font-700">{status.payments_paid}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">successful</p></div>
        <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center"><p className="font-display text-xl font-700">{doneCount}/{STEPS.length}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">steps done</p></div>
      </div>

      {!isLive && (
        <div className="space-y-2">
          {STEPS.map((s, i) => (
            <button
              key={s.t}
              onClick={() => toggle(i)}
              className="w-full flex items-start gap-3 text-left rounded-xl border border-[hsl(var(--border))] p-3 hover:bg-[hsl(var(--secondary))] transition-colors"
              data-testid={`golive-step-${i}`}
            >
              {checked[i]
                ? <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: 'hsl(var(--success))' }} />
                : <Circle className="h-5 w-5 shrink-0 mt-0.5 text-[hsl(var(--muted-foreground))]" />}
              <span>
                <span className={`text-sm font-600 ${checked[i] ? 'line-through opacity-60' : ''}`}>{i + 1}. {s.t}</span>
                <span className="block text-xs text-[hsl(var(--muted-foreground))] mt-0.5 leading-relaxed">{s.d}</span>
              </span>
            </button>
          ))}
          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-600 mt-1 hover:underline" style={{ color: 'hsl(var(--primary))' }} data-testid="golive-stripe-link">
            Open Stripe API keys <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      )}
    </div>
  );
}
